import { analyseTheme } from "../analyse/analyseTheme";
import { appPageRootFullHeight } from "../appBar/appBarTheme";
import { Theme } from "@material-ui/core/styles/createMuiTheme";

/** Shared tokens for list/table pages (batches, projects, users, …). */
export const tableTheme = {
  ...analyseTheme,
  secondarySoft: "rgba(255, 153, 102, 0.15)",
  headerBg: "#f8f9fb",
};

export const tableCheckboxModernStyle = {
  padding: 6,
};

/** Horizontal inset for first/last table cells (header + body). */
export const tableCellEdgePadding = 8;

/** Inner content height shared with action icon buttons. */
export const tableToolbarContentHeight = 28;

/** Outer control height: 28px content + 4px padding × 2 + 1px border × 2. */
export const tableToolbarControlHeight =
  tableToolbarContentHeight + 8 + 2;

/** Filled toolbar button: 28px content + 4px padding × 2 (no border). */
export const tableToolbarButtonHeight = tableToolbarContentHeight + 8;

export const tableToolbarPrimaryButtonStyle = (theme: Theme) => ({
  display: "inline-flex" as const,
  alignItems: "center" as const,
  gap: "6px",
  minWidth: "auto" as const,
  height: tableToolbarButtonHeight,
  minHeight: tableToolbarButtonHeight,
  maxHeight: tableToolbarButtonHeight,
  padding: "4px 12px",
  fontSize: "0.8125rem",
  fontWeight: 500,
  textTransform: "none" as const,
  lineHeight: `${tableToolbarContentHeight}px`,
  boxSizing: "border-box" as const,
  borderRadius: tableTheme.radiusSm,
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.secondary.contrastText,
  border: "none",
  boxShadow: "none",
  textDecoration: "none",
  "&:hover": {
    backgroundColor: theme.palette.secondary.dark,
    boxShadow: "none",
  },
});

export const tableToolbarOutlineButtonStyle = () => ({
  display: "inline-flex" as const,
  alignItems: "center" as const,
  gap: 6,
  minWidth: "auto" as const,
  height: tableToolbarControlHeight,
  minHeight: tableToolbarControlHeight,
  maxHeight: tableToolbarControlHeight,
  padding: "4px 12px",
  fontSize: "0.8125rem",
  fontWeight: 500,
  textTransform: "none" as const,
  lineHeight: `${tableToolbarContentHeight}px`,
  boxSizing: "border-box" as const,
  borderRadius: tableTheme.radiusSm,
  backgroundColor: tableTheme.surface,
  color: tableTheme.textPrimary,
  border: `1px solid ${tableTheme.border}`,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: tableTheme.headerBg,
    borderColor: tableTheme.secondaryLight,
    boxShadow: "none",
  },
  "&:disabled": {
    color: tableTheme.textSecondary,
    backgroundColor: tableTheme.surface,
  },
});

/** Row-level « Actions » dropdown (batches, projects, users tables). */
export const tableRowActionButtonModernStyle = (theme: Theme) => ({
  ...tableToolbarOutlineButtonStyle(),
  padding: "4px 20px",
  borderRadius: tableTheme.radius,
  borderColor: theme.palette.secondary.main,
  backgroundColor: "rgba(255, 153, 102, 0.03)",
  color: theme.palette.secondary.dark,
  "&:hover": {
    backgroundColor: "rgba(255, 153, 102, 0.08)",
    borderColor: theme.palette.secondary.main,
    color: theme.palette.secondary.dark,
    boxShadow: "none",
  },
  "&:disabled": {
    padding: "4px 24px",
    color: tableTheme.textSecondary,
    backgroundColor: tableTheme.headerBg,
    borderColor: tableTheme.border,
  },
});

/** Use height: 100% inside searchGroup — its border is included in tableToolbarControlHeight. */
export const tableToolbarGroupBoxStyle = {
  boxSizing: "border-box" as const,
  height: tableToolbarControlHeight,
};

/** Corner radii for first/last segments inside searchGroup (inner curve, 1px border inset). */
export const tableToolbarGroupCornerLeft = {
  borderTopLeftRadius: Math.max(tableTheme.radiusSm - 1, 0),
  borderBottomLeftRadius: Math.max(tableTheme.radiusSm - 1, 0),
};

export const tableToolbarGroupCornerRight = {
  borderTopRightRadius: Math.max(tableTheme.radiusSm - 1, 0),
  borderBottomRightRadius: Math.max(tableTheme.radiusSm - 1, 0),
};

export const tableControlModernStyle = {
  height: tableToolbarControlHeight,
  minHeight: tableToolbarControlHeight,
  padding: "4px 28px 4px 10px",
  lineHeight: `${tableToolbarContentHeight}px`,
  fontSize: "0.8125rem",
  border: `1px solid ${tableTheme.border}`,
  borderRadius: tableTheme.radiusSm,
  backgroundColor: tableTheme.surface,
  color: tableTheme.textPrimary,
  boxSizing: "border-box" as const,
};

export const tablePageToolbarStyles = (theme: Theme) => ({
  pageToolbar: {
    display: "flex",
    flexWrap: "wrap" as const,
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px 20px",
    padding: "0 0 12px 0",
    flexShrink: 0,
    width: "100%",
  },
  pageToolbarLeft: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    gap: "4px",
    minWidth: 0,
  },
  pageTitleRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap" as const,
    gap: "8px 16px",
    minWidth: 0,
  },
  pageToolbarRight: {
    display: "flex",
    flexWrap: "wrap" as const,
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "12px 16px",
  },
  pageTitleGroup: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  pageTitle: {
    fontSize: "1.25rem",
    fontWeight: 600,
    color: tableTheme.textPrimary,
    lineHeight: 1.3,
    margin: 0,
  },
  pageTitleCompact: {
    fontSize: "1.125rem",
  },
  pageSubtitle: {
    fontSize: "0.875rem",
    color: tableTheme.textSecondary,
    fontWeight: 400,
  },
  pageLoader: {
    flexShrink: 0,
  },
  pageLoaderSlot: {
    width: 20,
    height: 20,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  searchArea: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  },
  searchGroup: {
    display: "flex",
    alignItems: "stretch",
    ...tableToolbarGroupBoxStyle,
    border: `1px solid ${tableTheme.border}`,
    borderRadius: `${tableTheme.radiusSm}px`,
    overflow: "hidden",
    backgroundColor: tableTheme.surface,
    flexShrink: 0,
    position: "relative" as const,
  },
  searchModeInGroup: {
    width: 88,
    minWidth: 88,
    flexShrink: 0,
    height: "100%",
    minHeight: "100%",
    border: "none",
    borderRight: `1px solid ${tableTheme.border}`,
    ...tableToolbarGroupCornerLeft,
    backgroundColor: tableTheme.headerBg,
    boxShadow: "none",
  },
  searchFieldInGroup: {
    display: "flex",
    alignItems: "stretch",
    minWidth: 240,
    flex: "1 1 auto",
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
    position: "relative" as const,
    ...tableToolbarGroupCornerRight,
  },
  /** Search field filling the whole searchGroup (no mode select on the left). */
  searchFieldInGroupOnly: {
    display: "flex",
    alignItems: "stretch",
    minWidth: 240,
    flex: "1 1 auto",
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
    position: "relative" as const,
    borderRadius: `${Math.max(tableTheme.radiusSm - 1, 0)}px`,
  },
  pageTitleIconLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: tableTheme.textSecondary,
    textDecoration: "none",
    "&:hover": {
      color: tableTheme.secondaryDark,
    },
  },
  scopeSelectWrap: {
    width: 200,
    minWidth: 200,
    maxWidth: 200,
    flexShrink: 0,
  },
  scopeSelect: {
    width: "100%",
    display: "block",
  },
  searchModeSelect: {
    minWidth: 88,
    width: 88,
  },
  searchFieldWrapper: {
    minWidth: 240,
  },
});

export const tableFilterChipsStyles = (_theme: Theme) => ({
  filterChipsRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    alignItems: "center",
    gap: "8px",
    width: "100%",
    paddingBottom: 12,
  },
  filterChipsLabel: {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: tableTheme.textSecondary,
    flexShrink: 0,
  },
  filterChipsResetLink: {
    padding: 0,
    border: "none",
    background: "none",
    cursor: "pointer",
    fontSize: "0.75rem",
    fontWeight: 500,
    color: tableTheme.secondary,
    textDecoration: "underline",
    flexShrink: 0,
    "&:hover": {
      color: tableTheme.secondaryDark,
    },
  },
  filterChip: {
    height: 28,
    borderRadius: 13,
    backgroundColor: "rgba(255, 153, 102, 0.08)",
    color: tableTheme.textPrimary,
    fontSize: "0.75rem",
    fontWeight: 500,
    border: "1px solid rgba(255, 153, 102, 0.18)",
    "&:focus": {
      backgroundColor: "rgba(255, 153, 102, 0.08)",
    },
  },
  filterChipDeleteIcon: {
    color: tableTheme.secondaryLight,
    fontSize: 16,
    backgroundColor: "transparent !important",
    "&:hover": {
      color: tableTheme.secondary,
      backgroundColor: "transparent !important",
    },
  },
});

/** App bar clearance via `appContainerHeight` (spacing.unit × 8); subtract only main bottom padding. */
export const tablePageRootStyles = (theme: Theme) => appPageRootFullHeight(theme);

export const tablePageStyles = (theme: Theme) => ({
  tableWrapper: {
    flex: "1 1 auto",
    minHeight: 0,
    width: "100%",
    display: "flex",
    flexDirection: "column" as const,
  },
  tableCard: {
    display: "flex",
    flexDirection: "column" as const,
    flex: "1 1 auto",
    minHeight: 0,
    backgroundColor: tableTheme.surface,
    border: `1px solid ${tableTheme.border}`,
    borderRadius: tableTheme.radius,
    overflow: "hidden",
  },
  tableScrollArea: {
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
  },
  tableEmptyInCard: {
    textAlign: "center" as const,
    padding: theme.spacing.unit * 6,
    color: tableTheme.textSecondary,
  },
  modernCheckbox: tableCheckboxModernStyle,
});
